import React, { useEffect, useMemo, useState } from 'react'

import ConversationListHeader from 'conversations/components/NewChatSidebar/ConversationListHeader'
import ConversationSidebarSection from 'conversations/components/NewChatSidebar/ConversationSidebarSection'
import FilteredConversationList from 'conversations/components/NewChatSidebar/FilteredConversationList'
import SelectionPill from 'conversations/components/NewChatSidebar/SelectionPill'
import useCounts from 'conversations/hooks/useCounts'
import { useCurrentUserId } from 'conversations/hooks/useOrgUser'
import { useSectionQueryGroups } from 'conversations/hooks/useSectionQuery'
import { useSectionConfigByType, useSectionConfigProperty } from 'conversations/hooks/useSections'
import { getTeamsById } from 'conversations/modules/teams/selectors'
import { getActiveOrgUsers } from 'conversations/modules/user/selectors'
import { createConditionsArray, isQueueTimeSection } from 'conversations/utils/conditionsUtil'
import {
  buildConversationsQuery,
  PARTICIPANT_EMPTY_KEY,
} from 'conversations/utils/ConversationSearchUtil'
import { Pill, STATUSES_FOR_PILL } from 'conversations/utils/pillUtil'
import { useSelector } from 'react-redux'
import { FilterableQueryProps } from 'shared/typings/conversations/query'
import { ConversationSidebarSectionConfig } from 'shared/typings/conversationsSidebar/Filters'
import { SortOrder } from 'shared/typings/search/SortOrder'

import styles from './styles.module.css'

interface Props {
  inboxId: number | undefined
  sectionConfig?: ConversationSidebarSectionConfig
}
const allPillTypes: Pill[] = ['open', 'closed', 'all']
const noParticipantPillTypes: Pill[] = ['open']

interface SectionProps extends Props {
  sectionConfig: ConversationSidebarSectionConfig
}

export const Section: React.FC<SectionProps> = (props) => {
  switch (props.sectionConfig.type) {
    case 'compound':
    case null:
      return <CompoundSection {...props} />
    case 'my-convos':
      return <MyConversationSection {...props} />
    case 'all-convos':
      return <AllConversationsSection {...props} />
    case 'no-participants':
      return <NoParticipantsSection {...props} />
    default:
      return null
  }
}

const CompoundSection: React.FC<SectionProps> = ({
  inboxId,
  sectionConfig,
}): JSX.Element | null => {
  const teams = useSelector(getTeamsById)
  const activeOrgUsers = useSelector(getActiveOrgUsers)
  const queryGroups = useSectionQueryGroups(sectionConfig)

  const conditions = createConditionsArray(sectionConfig.condition)
  const isQueueTimeOnlySection = isQueueTimeSection(conditions)

  if (!isQueueTimeOnlySection && (!teams || !conditions || activeOrgUsers.length === 0)) {
    return null
  }

  return (
    <ConversationSection
      inboxId={inboxId}
      pillTypes={allPillTypes}
      queryGroups={queryGroups}
      sectionID={sectionConfig?.id}
      name={sectionConfig?.title}
    />
  )
}

export const AllConversationsSection: React.FC<Props> = ({ inboxId }): JSX.Element => {
  const sectionConfig = useSectionConfigByType('all-convos')
  const query = useMemo(() => {
    return [{}]
  }, [])

  return (
    <ConversationSection
      inboxId={inboxId}
      pillTypes={allPillTypes}
      sectionID={sectionConfig?.id}
      queryGroups={query}
      name="All conversations"
    />
  )
}

export const MyConversationSection: React.FC<Props> = ({ inboxId }): JSX.Element => {
  const currentUserId = useCurrentUserId()
  const sectionConfig = useSectionConfigByType('my-convos')

  const query = useMemo(() => {
    return [
      {
        participantIds: [[currentUserId ?? 0]],
      },
    ]
  }, [currentUserId])

  return (
    <ConversationSection
      sectionID={sectionConfig?.id}
      inboxId={inboxId}
      pillTypes={allPillTypes}
      queryGroups={query}
      name="My conversations"
      initiallyOpen={true}
    />
  )
}

export const NoParticipantsSection: React.FC<Props> = ({ inboxId }): JSX.Element => {
  const sectionConfig = useSectionConfigByType('no-participants')
  const query = useMemo(() => {
    return [
      {
        participantIds: [[PARTICIPANT_EMPTY_KEY]],
      },
    ]
  }, [])

  return (
    <ConversationSection
      sectionID={sectionConfig?.id}
      inboxId={inboxId}
      pillTypes={noParticipantPillTypes}
      queryGroups={query}
      name="No participants"
    />
  )
}

interface ConversationSectionProps {
  inboxId?: number
  pillTypes: Pill[]
  name: string
  queryGroups: FilterableQueryProps[]
  initiallyOpen?: boolean
  uuid?: string
  sectionID?: string
}
const DEFAULT_LIST_LENGTH = 5

const ConversationSection: React.FC<ConversationSectionProps> = ({
  inboxId,
  queryGroups,
  pillTypes,
  name,
  initiallyOpen,
  sectionID,
}): JSX.Element => {
  const [selectedPill, setSelectedPill] = useState<Pill>('open')
  const length = useSectionConfigProperty(sectionID, 'sectionLength') ?? DEFAULT_LIST_LENGTH
  const subscriptionId = useSectionConfigProperty(sectionID, 'subscriptionId') ?? ''
  const [sectionLength, setSectionLength] = useState<number>(length)
  const onSectionLengthChange = (length: number) => setSectionLength(length)

  const statusesForPill = STATUSES_FOR_PILL[selectedPill]
  const sortOrder = queryGroups.some((g) => g.routedNotRespondedTime)
    ? SortOrder.ASC
    : SortOrder.DESC
  const queryWithStatus = useMemo(
    () =>
      buildConversationsQuery({
        statuses: statusesForPill,
        inboxId,
        sortOrder,
      }),
    [sortOrder, statusesForPill, inboxId]
  )
  useEffect(() => {
    setSelectedPill('open')
  }, [inboxId])

  const viewType = sectionID
    ? `${sectionID}-${selectedPill}`
    : `${name.replace(' ', '')}-${inboxId ?? 'all'}-${selectedPill}`

  return (
    <ConversationSidebarSection
      sectionID={sectionID}
      selectedPill={selectedPill}
      initiallyOpen={initiallyOpen}
      renderSectionHeader={(props) => (
        <ConversationListHeader
          data-ocweb-id="ConversationListHeader"
          sectionID={sectionID}
          name={name}
          inboxId={inboxId}
          onSectionLengthChange={onSectionLengthChange}
          {...props}
        />
      )}>
      <>
        <SectionPills
          pillTypes={pillTypes}
          sectionUUID={sectionID}
          setSelectedPill={setSelectedPill}
          selectedPill={selectedPill}
          subscriptionId={subscriptionId}
        />
        <FilteredConversationList
          selectedStatus={selectedPill}
          sectionID={sectionID}
          query={queryWithStatus}
          viewType={viewType}
          inboxId={inboxId}
          sectionLength={sectionLength}
          onSectionLengthChange={onSectionLengthChange}
        />
      </>
    </ConversationSidebarSection>
  )
}

interface SectionPillsProps {
  pillTypes: Pill[]
  sectionUUID?: string
  setSelectedPill: (pill: Pill) => void
  selectedPill: Pill
  subscriptionId: string
}

const SectionPills: React.FC<SectionPillsProps> = ({
  pillTypes,
  sectionUUID,
  setSelectedPill,
  selectedPill,
  subscriptionId,
}) => {
  const countsByPillType = useCounts(pillTypes, subscriptionId, sectionUUID)
  return (
    <div className={styles.selectionPillsContainer}>
      {pillTypes.map((pillType) => (
        <SelectionPill
          key={pillType}
          count={countsByPillType[pillType]}
          onClick={() => setSelectedPill(pillType)}
          selected={selectedPill === pillType}>
          {pillType}
        </SelectionPill>
      ))}
    </div>
  )
}
